import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Send, RefreshCw, Save, PlugZap } from 'lucide-react';
import { Button, IconButton } from '../UI';
import PhoneInput from '../PhoneInput';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';

// Avisos internos por WhatsApp (Evolution API). Duas partes:
// 1) "Meus avisos": cada admin escolhe o próprio número e quais eventos recebe (/users/me/whatsapp-alerts).
// 2) "Integração": URL/instância/chave da Evolution e hora do resumo — só super_admin edita (/settings/whatsapp).
// Tem botões de salvar próprios; o "Salvar Configurações" geral da página não se aplica a esta aba.

const EVENT_LABELS: Record<string, { label: string; hint: string }> = {
  quiz_lead: { label: 'Lead VIP / Qualificado no quiz', hint: 'Assim que o lead termina o quiz da landing page' },
  crm_lead: { label: 'Cliente novo no CRM', hint: 'Quando outro admin cadastra um cliente' },
  contract_form_submitted: { label: 'Dados de contrato preenchidos', hint: 'Cliente enviou o formulário do contrato' },
  contract_signed: { label: 'Contrato assinado', hint: 'Cliente assinou pelo link' },
  contract_expired: { label: 'Assinatura expirada', hint: 'Cliente abriu o link de assinatura depois do prazo' },
  documents_submitted: { label: 'Documentos enviados', hint: 'Cliente enviou a checklist de documentos' },
  quote_accepted: { label: 'Orçamento aceito', hint: '' },
  quote_declined: { label: 'Orçamento recusado', hint: '' },
  form_submitted: { label: 'Formulários públicos', hint: 'Pedido de NIF, análise de visto ou de residência' },
  booking: { label: 'Agendamentos', hint: 'Reunião marcada, remarcada ou cancelada pelo cliente' },
  daily_digest: { label: 'Resumo diário de pendências', hint: 'Assinaturas pendentes/expiradas, leads sem contato, pagamentos, documentos…' }
};

const STATE_LABEL: Record<string, { text: string; className: string }> = {
  open: { text: 'Conectado', className: 'bg-green-100 text-green-800' },
  connecting: { text: 'Conectando…', className: 'bg-amber-100 text-amber-800' },
  close: { text: 'Desconectado — leia o QR Code de novo', className: 'bg-red-100 text-red-800' },
  not_configured: { text: 'Não configurado', className: 'bg-gray-100 text-gray-700' },
  error: { text: 'Erro ao consultar', className: 'bg-red-100 text-red-800' }
};

const SERVER_DEFAULT = 'Vazio = configuração do servidor';

// O navegador costuma preencher instância/chave com o e-mail e a senha do login do admin.
// Campo readOnly não é preenchido automaticamente; libera a edição no foco.
const noAutofill = {
  readOnly: true,
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.readOnly = false;
  },
  'data-lpignore': 'true',
  'data-1p-ignore': 'true'
};

interface Prefs {
  phone: string;
  enabled: boolean;
  events: string[];
  availableEvents: string[];
}

interface Integration {
  enabled: boolean;
  baseUrl: string;
  apiKey: string;
  instance: string;
  digestHour: number;
  lastDigestAt: string | null;
  envFallback: boolean;
}

const WhatsappAlertsSettings: React.FC = () => {
  const notifications = useNotifications();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [integration, setIntegration] = useState<Integration | null>(null);
  const [status, setStatus] = useState<{ state: string; error?: string } | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [phoneValid, setPhoneValid] = useState(true);
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const errMsg = (err: unknown, fallback: string) =>
    (axios.isAxiosError(err) && (err.response?.data as { message?: string } | undefined)?.message) || fallback;

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get('/settings/whatsapp/status');
      setStatus(res.data);
    } catch {
      setStatus({ state: 'error' });
    }
  }, []);

  useEffect(() => {
    axios.get('/users/me/whatsapp-alerts').then((res) => setPrefs(res.data)).catch(() => {});
    axios.get('/settings/whatsapp').then((res) => setIntegration(res.data)).catch(() => {});
    loadStatus();
  }, [loadStatus]);

  const toggleEvent = (key: string) => {
    if (!prefs) return;
    const events = prefs.events.includes(key) ? prefs.events.filter((e) => e !== key) : [...prefs.events, key];
    setPrefs({ ...prefs, events });
  };

  const savePrefs = async () => {
    if (!prefs) return;
    setSavingPrefs(true);
    try {
      const res = await axios.put('/users/me/whatsapp-alerts', {
        phone: prefs.phone,
        enabled: prefs.enabled,
        events: prefs.events
      });
      setPrefs(res.data);
      notifications.showSuccess('Salvo', 'Seus avisos por WhatsApp foram atualizados.');
    } catch (err) {
      notifications.showError('Erro', errMsg(err, 'Não foi possível salvar.'));
    } finally {
      setSavingPrefs(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post('/settings/whatsapp/test');
      if (res.data.simulated) {
        notifications.showWarning('Modo simulado', 'A Evolution API não está configurada; a mensagem só foi registrada no log do servidor.');
      } else {
        notifications.showSuccess('Enviado', 'Confira o seu WhatsApp.');
      }
    } catch (err) {
      notifications.showError('Erro', errMsg(err, 'Falha ao enviar o teste.'));
    } finally {
      setTesting(false);
    }
  };

  const saveIntegration = async () => {
    if (!integration) return;
    setSavingIntegration(true);
    try {
      const res = await axios.put('/settings/whatsapp', integration);
      setIntegration(res.data);
      notifications.showSuccess('Salvo', 'Integração do WhatsApp atualizada.');
      loadStatus();
    } catch (err) {
      notifications.showError('Erro', errMsg(err, 'Não foi possível salvar a integração.'));
    } finally {
      setSavingIntegration(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post('/settings/whatsapp/digest-now');
      notifications.showSuccess('Resumo enviado', `${res.data.sent || 0} admin(s) receberam o resumo.`);
    } catch (err) {
      notifications.showError('Erro', errMsg(err, 'Falha ao enviar o resumo.'));
    } finally {
      setSendingDigest(false);
    }
  };

  if (!prefs || !integration) {
    return <p className="text-sm text-gray-500">Carregando…</p>;
  }

  const stateInfo = STATE_LABEL[status?.state || ''] || { text: status?.state || '—', className: 'bg-gray-100 text-gray-700' };
  const events = prefs.availableEvents.filter((key) => EVENT_LABELS[key]);

  return (
    <div className="space-y-10">
      {/* Meus avisos */}
      <section className="space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Meus avisos por WhatsApp</h3>
          <p className="text-sm text-gray-500">
            Escolha o número e o que você quer receber. Cada admin configura os próprios avisos.
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Seu celular (escolha o país)</span>
          <div id="wa-phone" className="sm:max-w-sm">
            <PhoneInput
              lang="pt"
              defaultCountry="PT"
              initialValue={prefs.phone}
              ariaLabel="Seu celular"
              countryAriaLabel="País (DDI)"
              onChange={(e164, valid) => {
                setPhoneValid(valid || !e164);
                setPrefs((p) => (p ? { ...p, phone: e164 } : p));
              }}
            />
            {!phoneValid && <p className="mt-1 text-xs text-red-600">Celular inválido para o país escolhido.</p>}
          </div>
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="check-input"
            checked={prefs.enabled}
            onChange={(e) => setPrefs({ ...prefs, enabled: e.target.checked })}
          />
          <span className="text-sm font-medium text-gray-700">Receber avisos no WhatsApp</span>
        </label>

        <fieldset disabled={!prefs.enabled} className="space-y-3 disabled:opacity-50">
          <legend className="text-sm font-medium text-gray-700 mb-2">Quero ser avisado de:</legend>
          {events.map((key) => (
            <label key={key} className="flex items-start gap-3">
              <input
                type="checkbox"
                className="check-input mt-0.5"
                checked={prefs.events.includes(key)}
                onChange={() => toggleEvent(key)}
              />
              <span>
                <span className="block text-sm text-gray-800">{EVENT_LABELS[key].label}</span>
                {EVENT_LABELS[key].hint && <span className="block text-xs text-gray-500">{EVENT_LABELS[key].hint}</span>}
              </span>
            </label>
          ))}
        </fieldset>

        <div className="flex flex-wrap gap-3">
          <Button onClick={savePrefs} loading={savingPrefs} disabled={!phoneValid} icon={Save}>
            Salvar meus avisos
          </Button>
          <Button variant="outline" onClick={sendTest} loading={testing} icon={Send}>
            Enviar teste
          </Button>
        </div>
      </section>

      {/* Integração */}
      <section className="space-y-5 border-t border-gray-200 pt-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Integração (Evolution API)</h3>
            <p className="text-sm text-gray-500">
              Número que envia os avisos. {isSuperAdmin ? '' : 'Só o super admin altera estes campos.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${stateInfo.className}`}>
              <PlugZap className="w-3.5 h-3.5" />
              {stateInfo.text}
            </span>
            <IconButton icon={RefreshCw} label="Atualizar status" onClick={loadStatus} />
          </div>
        </div>

        <fieldset disabled={!isSuperAdmin} className="grid grid-cols-1 sm:grid-cols-2 gap-4 disabled:opacity-60">
          <label className="flex items-center gap-3 sm:col-span-2">
            <input
              type="checkbox"
              className="check-input"
              checked={integration.enabled}
              onChange={(e) => setIntegration({ ...integration, enabled: e.target.checked })}
            />
            <span className="text-sm font-medium text-gray-700">Envio ativo</span>
          </label>
          <div className="sm:col-span-2">
            <label htmlFor="wa-url" className="block text-sm font-medium text-gray-700 mb-1">URL da Evolution API</label>
            <input
              id="wa-url"
              name="evolution-url"
              autoComplete="off"
              className="field w-full"
              placeholder={SERVER_DEFAULT}
              value={integration.baseUrl}
              onChange={(e) => setIntegration({ ...integration, baseUrl: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="wa-instance" className="block text-sm font-medium text-gray-700 mb-1">Instância</label>
            <input
              id="wa-instance"
              name="evolution-instance"
              autoComplete="off"
              {...noAutofill}
              className="field w-full"
              placeholder={SERVER_DEFAULT}
              value={integration.instance}
              onChange={(e) => setIntegration({ ...integration, instance: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="wa-key" className="block text-sm font-medium text-gray-700 mb-1">API key</label>
            <input
              id="wa-key"
              name="evolution-api-key"
              type="password"
              autoComplete="new-password"
              {...noAutofill}
              className="field w-full"
              placeholder={SERVER_DEFAULT}
              value={integration.apiKey}
              onChange={(e) => setIntegration({ ...integration, apiKey: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="wa-hour" className="block text-sm font-medium text-gray-700 mb-1">
              Hora do resumo diário (Lisboa)
            </label>
            <select
              id="wa-hour"
              className="field field--select w-full"
              value={integration.digestHour}
              onChange={(e) => setIntegration({ ...integration, digestHour: Number(e.target.value) })}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500 self-end">
            {integration.lastDigestAt
              ? `Último resumo: ${new Date(integration.lastDigestAt).toLocaleString('pt-PT')}`
              : 'Nenhum resumo enviado ainda.'}
          </div>
          {integration.envFallback && !integration.baseUrl && (
            <p className="text-xs text-gray-500 sm:col-span-2">
              Campos vazios usam as variáveis EVOLUTION_* do servidor.
            </p>
          )}
        </fieldset>

        {isSuperAdmin && (
          <div className="flex flex-wrap gap-3">
            <Button onClick={saveIntegration} loading={savingIntegration} icon={Save}>
              Salvar integração
            </Button>
            <Button variant="outline" onClick={sendDigestNow} loading={sendingDigest} icon={Send}>
              Enviar resumo agora
            </Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default WhatsappAlertsSettings;
